import React from "react"
import { Link } from "gatsby"
import GuideLayout from "../../components/guide/guide-layout"
import { findGuide } from "../../data/guides"

const guide = findGuide("how-much-sound-insulation-does-a-phone-booth-need")

const Page = () => (
  <GuideLayout guide={guide}>
    <p>
      Every booth on the market quotes a decibel figure, and the figures are hard to compare because they do not all measure the same thing. This guide explains what the numbers mean, which one you should ask for, and how much reduction actually makes a difference in an open-plan office.
    </p>

    <h2>Start with the problem: speech</h2>
    <p>
      The sound that disturbs people in an open office is not printers or air conditioning. It is other people talking, and above all one side of a phone call. Normal conversation measured at one metre is about 60 dB(A). A typical open-plan office has a background level between 45 and 55 dB(A). A phone call therefore sits well above the background, and because speech carries meaning, the brain keeps tuning in to it. Research on open-plan offices consistently finds intelligible speech to be the single largest cause of lost concentration.
    </p>
    <p>
      A booth has two jobs. It has to make the person inside inaudible, or at least unintelligible, to the people outside. And it has to make the office quiet enough for the person inside to hear their call. Both jobs are about reducing speech, not eliminating every sound.
    </p>

    <h2>Decibels behave strangely</h2>
    <p>
      Decibels are logarithmic. A reduction of 10 dB is perceived as roughly halving the loudness. A reduction of 20 dB feels like a quarter. So the difference between a booth that reduces speech by 25 dB and one that reduces it by 30 dB is not "a bit better": it is noticeably quieter, and it is the difference between hearing words and hearing a murmur.
    </p>
    <table>
      <thead>
        <tr>
          <th>Reduction</th>
          <th>What a 60 dB voice sounds like outside</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>15 dB</td><td>Clearly audible, most words understood</td></tr>
        <tr><td>20 dB</td><td>Audible, many words understood</td></tr>
        <tr><td>25 dB</td><td>Voice noticed, words mostly lost in office background</td></tr>
        <tr><td>30 dB</td><td>Faint murmur, no intelligible speech</td></tr>
        <tr><td>33 dB and above</td><td>Barely detectable in a normal office</td></tr>
      </tbody>
    </table>

    <h2>Three numbers that get called "dB"</h2>
    <h3>Rw, the panel rating</h3>
    <p>
      Rw (weighted sound reduction index) is a laboratory rating for a single building element, such as a wall panel or a glass pane. It is measured with the panel sealed into an opening between two rooms, so it tells you how good the material is, not how good the finished booth is. A booth is a box with a door, seals, a fan and a cable pass-through, and sound finds every gap. Quoting the Rw of the wall as the performance of the booth is common and misleading.
    </p>
    <h3>Dw or DnT,w, the room rating</h3>
    <p>
      These are field measurements between two spaces and include the effect of gaps and flanking paths. They are more honest than Rw, but they were designed for rooms, and the way they are measured varies from booth to booth.
    </p>
    <h3>Speech level reduction, the booth rating</h3>
    <p>
      In 2020 the ISO published a standard written specifically for enclosed furniture: ISO 23351-1. It measures the reduction of the A-weighted speech level of a person inside the booth as heard outside, in a way that includes the door, the seals and the ventilation openings. The result is a single figure called speech level reduction, D<sub>S,A</sub>, and it is sorted into classes:
    </p>
    <table>
      <thead>
        <tr>
          <th>ISO 23351-1 class</th>
          <th>Speech level reduction</th>
          <th>In practice</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>A+</td><td>33 dB or more</td><td>Confidential calls in a quiet office</td></tr>
        <tr><td>A</td><td>30 to 33 dB</td><td>Calls not intelligible outside; the usual target for a phone booth</td></tr>
        <tr><td>B</td><td>25 to 30 dB</td><td>Good for focus work and everyday calls in a busy office</td></tr>
        <tr><td>C</td><td>20 to 25 dB</td><td>Reduces distraction, does not give privacy</td></tr>
        <tr><td>D</td><td>15 to 20 dB</td><td>Screens more than booths</td></tr>
      </tbody>
    </table>
    <p>
      If you compare booths on one number, make it this one, and ask which class the whole booth was measured at, with its own fan running. A booth in class B is a perfectly good choice for most open-plan offices. Class A is the target if the calls are confidential, for example HR, legal or client work in a small firm.
    </p>

    <h2>Why ventilation and insulation fight each other</h2>
    <p>
      A sealed box is the easiest way to stop sound and the fastest way to make a booth unusable. One person in one cubic metre raises the temperature and the carbon dioxide level within minutes. Every booth therefore has openings for air, and every opening is a path for sound. The quality of a booth lies in how the air path is designed: long, lined ducts with bends absorb sound while letting air through, and a fan that is quiet itself does not add to the noise inside.
    </p>
    <p>
      Ask two questions: how many air changes per hour does the fan deliver, and what is the noise level inside the booth with the fan running? A figure around 30 to 35 dB(A) inside is quiet enough for a call. Anything above 40 dB(A) is noticeable.
    </p>

    <h2>The wall, briefly</h2>
    <p>
      Good booth walls are sandwiches. Mass stops sound, so a dense outer layer helps; a soft absorbent core turns sound energy into heat; and decoupling the layers stops vibration travelling straight through. <Link to="/vibra-one/">Vibra One</Link> uses laminated MDF for mass, mineral wool as the absorbent core and a sound-absorbing felt lining, on a wooden frame. Inside, the felt also stops the booth sounding like a box, which is what makes a call feel natural rather than echoey.
    </p>
    <p>
      Glass is the weak point of any booth, because it is thin and stiff. Laminated acoustic glass with an interlayer performs far better than a single pane of the same thickness. The door matters even more: look for full-perimeter seals and a closing mechanism that pulls the door tight rather than letting it rest on a magnet.
    </p>

    <h2>What to ask a supplier</h2>
    <ol>
      <li>Which ISO 23351-1 class has the complete booth been measured at, and can you see the report?</li>
      <li>What is the noise level inside with the ventilation running?</li>
      <li>How many air changes per hour, and does the fan run on a sensor or constantly?</li>
      <li>What are the door seals made of and how are they replaced when they wear?</li>
      <li>Can you sit in one for twenty minutes? No specification replaces that test, which is why we offer a <Link to="/virtual-demo/">demo</Link> before any order.</li>
    </ol>
    <p className="note">
      Sources: ISO 23351-1:2020, Acoustics: Measurement of speech level reduction of furniture ensembles and enclosures; ISO 717-1 for Rw; typical office noise levels from published workplace acoustics research. Last reviewed September 2026.
    </p>
  </GuideLayout>
)

export default Page
